/**
 * Session management utilities
 * Handles localStorage/sessionStorage for auth tokens and user data
 */

import { User } from "../types";

export interface Session {
  accessToken: string;
  refreshToken: string;
  user: User;
}

const STORAGE_KEYS = {
  ACCESS_TOKEN: "accessToken",
  REFRESH_TOKEN: "refreshToken",
  USER: "user",
};

/**
 * Load session from localStorage
 * @returns Session object or null if no session exists
 */
export async function loadSession(): Promise<Session | null> {
  try {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);

    if (!accessToken || !refreshToken || !userStr) {
      return null;
    }

    const user = JSON.parse(userStr) as User;

    return {
      accessToken,
      refreshToken,
      user,
    };
  } catch (error) {
    console.error("Error loading session:", error);
    return null;
  }
}

/**
 * Save session to localStorage
 * @param session - Session data including tokens and user info
 */
export async function saveSession(session: Session): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, session.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, session.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(session.user));
  } catch (error) {
    console.error("Error saving session:", error);
    throw error;
  }
}

/**
 * Clear all session data from localStorage
 */
export async function clearSession(): Promise<void> {
  try {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
  } catch (error) {
    console.error("Error clearing session:", error);
  }
}

/**
 * Get access token from localStorage
 * @returns Access token or null
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
}

/**
 * Get refresh token from localStorage
 * @returns Refresh token or null
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
}

/**
 * Get stored user data from localStorage
 * @returns User object or null
 */
export function getStoredUser(): User | null {
  try {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error("Error getting stored user:", error);
    return null;
  }
}
